import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { Header } from 'components/common/header/Header';
import { useMeetingDetail, useMeetingMembers } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { MeetingRepository } from 'repository/meetingRepository';
import { SubPlaceModal, SubPlace, SubPlaceForm, getCategoryLabel, getCategoryIcon } from 'components/meeting/SubPlaceModal';
import './MeetingDetail.css';

interface Participant {
    id: number;
    name: string;
    status: 'confirmed' | 'pending';
    isHost: boolean;
}

export const MeetingDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert, showConfirm } = useAlert();
    const { meetingId: rawMeetingId } = useParams();

    // meetingId 타입 검증 (useMemo로 메모이제이션)
    const meetingId = useMemo(() => {
        const id = typeof rawMeetingId === 'string' ? rawMeetingId : undefined;
        if (!id) {
            console.error('Invalid meetingId:', rawMeetingId, 'Type:', typeof rawMeetingId);
        }
        return id;
    }, [rawMeetingId]);

    const { data: meetingData } = useMeetingDetail();
    const { data: membersData } = useMeetingMembers();
    const [activeTab, setActiveTab] = useState<'info' | 'participants'>('info');
    const [attendanceStatus, setAttendanceStatus] = useState<'attending' | 'not_attending' | 'undecided'>('undecided');
    const [isLocationSharing, setIsLocationSharing] = useState(() => {
        // localStorage에서 위치공유 설정 불러오기
        const saved = localStorage.getItem(`locationSharing_${meetingId}`);
        return saved === 'true';
    });
    const [showShareModal, setShowShareModal] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);

    // 서브 장소 관련 상태
    const [subPlaces, setSubPlaces] = useState<SubPlace[]>([]);
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm | undefined>(undefined);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(false);
    const [locationStateProcessed, setLocationStateProcessed] = useState(false);

    // 컴포넌트 마운트 시 스크롤 위치 초기화
    useEffect(() => {
        window.scrollTo(0, 0);
        // 내부 스크롤 컨테이너도 초기화
        const content = document.querySelector('.meeting-detail-content');
        if (content) {
            content.scrollTop = 0;
        }
    }, [meetingId]);

    // 컴포넌트 마운트 시 모임 상세 및 멤버 정보 조회
    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
            meetingStore.onGetMeetingMembers(meetingId);
        }
    }, [meetingId]);

    // API 데이터 변환
    const meeting = useMemo(() => {
        if (!meetingData?.data) {
            return {
                id: meetingId,
                name: '로딩 중...',
                date: '',
                time: '',
                location: '',
                locationDetail: '',
                description: '',
                status: 'upcoming' as const,
                inviteCode: '',
                createdBy: '',
                isRegularMeeting: false,
                repeatType: null as string | null,
                repeatInterval: null as number | null
            };
        }

        const data = meetingData.data;

        // 스케줄 정보 디버깅
        console.log('📅 MeetingDetail 스케줄 정보:', {
            hasSchedule: !!data.schedule,
            schedule: data.schedule,
            startDatetime: data.schedule?.startDatetime
        });

        return {
            id: data.meetingId,
            name: data.meetingName,
            date: data.schedule?.startDatetime ?
                new Date(data.schedule.startDatetime).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' }) :
                '일정 미정',
            time: data.schedule?.startDatetime ?
                new Date(data.schedule.startDatetime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) :
                '',
            location: data.placeName || '장소 미정',
            locationDetail: data.placeAddress || '',
            description: data.description || '',
            status: data.status?.toLowerCase() || 'upcoming',
            inviteCode: data.inviteCode || '',
            createdBy: data.creatorNickname || '알 수 없음',
            isRegularMeeting: data.isRegularMeeting || false,
            repeatType: data.repeatType || null,
            repeatInterval: data.repeatInterval || null,
            // 여행 관련 필드
            meetingType: data.meetingType || 'DAY',
            travelDestination: data.travelDestination || '',
            travelType: data.travelType || '',
            travelStartDate: data.travelStartDate ? new Date(data.travelStartDate) : null,
            travelEndDate: data.travelEndDate ? new Date(data.travelEndDate) : null,
            meetingTime: data.meetingTime || '',
            hotelPlaceId: data.hotelPlaceId || '',
            hotelName: data.hotelName || '',
            hotelAddress: data.hotelAddress || ''
        };
    }, [meetingData, meetingId]);

    // 여행 모임인지 확인
    const isTravelMeeting = meeting.meetingType === 'TRAVEL';

    // 여행 기간 계산
    const travelPeriod = useMemo(() => {
        if (!meeting.travelStartDate || !meeting.travelEndDate) return null;
        const start = meeting.travelStartDate;
        const end = meeting.travelEndDate;
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = days - 1;
        return { days, nights, start, end };
    }, [meeting.travelStartDate, meeting.travelEndDate]);

    // 서브 장소 목록 조회
    const fetchSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        try {
            const response = await MeetingRepository.onGetSubPlaces(meetingId);
            console.log('서브 장소 목록 조회 응답:', response);
            // API 응답 구조: response.data 또는 response.data.data
            const subPlaceList = response?.data?.data || response?.data;
            if (Array.isArray(subPlaceList)) {
                setSubPlaces(subPlaceList);
            } else {
                setSubPlaces([]);
            }
        } catch (error) {
            console.error('서브 장소 조회 실패:', error);
            setSubPlaces([]);
        }
    }, [meetingId]);

    // 모임 상세 조회 후 서브 장소 조회
    useEffect(() => {
        if (meetingId && meetingData?.data) {
            fetchSubPlaces();
        }
    }, [meetingId, meetingData, fetchSubPlaces]);

    // TimePicker 또는 PlaceSearch에서 돌아온 경우 처리 (한 번만 실행)
    useEffect(() => {
        if (!location.state?.fromSubPlaceModal || locationStateProcessed) return;

        const savedForm = location.state.subPlaceForm;
        const savedIsCustomTime = location.state.isCustomVisitTime || false;

        // TimePicker에서 돌아온 경우
        if (location.state.selectedTime && savedForm) {
            setSubPlaceForm({
                ...savedForm,
                visitTime: location.state.selectedTime
            });
            setIsCustomVisitTime(true);
        }
        // PlaceSearch에서 돌아온 경우
        else if (location.state.placeInfo) {
            const placeInfo = location.state.placeInfo;
            const defaultForm = { placeName: '', placeAddress: '', category: isTravelMeeting ? 'ATTRACTION' : 'PRIMARY', dayNumber: 1, visitTime: '', description: '' };
            setSubPlaceForm({
                ...(savedForm || defaultForm),
                placeName: placeInfo.name || savedForm?.placeName || '',
                placeAddress: placeInfo.address || savedForm?.placeAddress || ''
            });
            setIsCustomVisitTime(savedIsCustomTime);
        }
        // 기타 (폼 데이터만 복원)
        else if (savedForm) {
            setSubPlaceForm(savedForm);
            setIsCustomVisitTime(savedIsCustomTime);
        }

        // 수정 중이던 서브 장소가 있으면 복원
        if (location.state.editingSubPlaceId) {
            const editingPlace = subPlaces.find(sp => sp.subPlaceId === location.state.editingSubPlaceId);
            if (editingPlace) {
                setEditingSubPlace(editingPlace);
            }
        }

        setShowSubPlaceModal(true);
        setLocationStateProcessed(true);
    }, [location.state, subPlaces, isTravelMeeting, locationStateProcessed]);

    const participants: Participant[] = useMemo(() => {
        return membersData?.data?.map((member: any) => ({
            id: member.userId,
            name: member.nickname || member.userId || '사용자',
            status: member.attendanceStatus === 'ATTENDING' ? 'confirmed' : 'pending',
            isHost: member.role === 'CREATOR'
        })) || [];
    }, [membersData]);

    // 1시간 이내 시작하거나 진행 중인 모임인지 확인 (종료 시간까지 배너 노출)
    const upcomingSoonInfo = useMemo(() => {
        if (!meetingData?.data?.schedule?.startDatetime) return null;

        const now = new Date();
        const meetingTime = new Date(meetingData.data.schedule.startDatetime);
        const endTime = meetingData.data.schedule.endDatetime
            ? new Date(meetingData.data.schedule.endDatetime)
            : null;

        // 종료 시간이 지났으면 표시하지 않음
        if (endTime && endTime < now) return null;

        const diffMs = meetingTime.getTime() - now.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        // 이미 시작했지만 아직 종료되지 않은 경우
        if (diffMinutes <= 0) {
            return { timeText: null }; // 진행 중
        }

        // 1시간 이내 시작 예정
        if (diffMinutes <= 60) {
            return {
                timeText: diffMinutes < 60 ? `${diffMinutes}분` : '1시간'
            };
        }
        return null;
    }, [meetingData?.data?.schedule?.startDatetime, meetingData?.data?.schedule?.endDatetime]);

    // 시간 디버깅 로그 (필요시에만)
    useEffect(() => {
        if (meetingData?.data?.schedule?.startDatetime) {
            console.log('=== MeetingDetail 시간 표시 디버깅 ===');
            console.log('API에서 받은 startDatetime:', meetingData.data.schedule.startDatetime);
            const startDate = new Date(meetingData.data.schedule.startDatetime);
            console.log('Date 객체:', startDate.toString());
            console.log('toLocaleString:', startDate.toLocaleString('ko-KR'));
            console.log('toLocaleTimeString:', startDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }));
        }
    }, [meetingData?.data?.schedule?.startDatetime]);

    // 현재 사용자의 참석 상태 확인 및 설정
    useEffect(() => {
        const currentUserId = sessionStorage.getItem('userId') || 'mock-user-001';
        const currentMember = membersData?.data?.find((m: any) => m.userId === currentUserId);

        if (currentMember) {
            const status = currentMember.attendanceStatus;
            if (status === 'ATTENDING') {
                setAttendanceStatus('attending');
            } else if (status === 'NOT_ATTENDING') {
                setAttendanceStatus('not_attending');
            } else {
                setAttendanceStatus('undecided');
            }
        }
    }, [membersData]);

    const handleEdit = () => {
        navigate(`/meeting/edit/${meetingId}`);
    };

    const handleShare = () => {
        setShowShareModal(true);
    };

    const handleCopyInviteCode = async () => {
        await navigator.clipboard.writeText(meeting.inviteCode);
        showAlert({ message: '초대 코드가 복사되었습니다!' });
    };

    const handleCopyLink = async () => {
        const link = `${window.location.origin}/meeting/invite/${meeting.inviteCode}`;
        await navigator.clipboard.writeText(link);
        showAlert({ message: '링크가 복사되었습니다!' });
    };

    const handleShareKakao = () => {
        const link = `${window.location.origin}/meeting/invite/${meeting.inviteCode}`;
        showAlert({
            title: '카카오톡 공유',
            message: `카카오톡 공유 기능은 준비 중입니다.\n링크: ${link}`
        });
    };

    const handleShareSMS = () => {
        const link = `${window.location.origin}/meeting/invite/${meeting.inviteCode}`;
        const message = `[${meeting.name}] 모임에 초대합니다!\n초대코드: ${meeting.inviteCode}\n링크: ${link}`;
        window.location.href = `sms:?body=${encodeURIComponent(message)}`;
    };

    const handleRealtimeLocation = () => {
        navigate(`/meeting/location/${meetingId}`);
    };

    const handleChat = () => {
        navigate(`/chat/room/${meetingId}`);
    };

    // 서브 장소 추가 모달 열기
    const handleAddSubPlace = () => {
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 수정 모달 열기
    const handleEditSubPlace = (subPlace: SubPlace) => {
        setEditingSubPlace(subPlace);
        setSubPlaceForm(undefined); // SubPlaceModal will populate from editingSubPlace
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 모달 닫기
    const handleCloseSubPlaceModal = () => {
        setShowSubPlaceModal(false);
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setLocationStateProcessed(false);
    };

    // 서브 장소 삭제
    const handleDeleteSubPlace = async (subPlace: SubPlace) => {
        if (!meetingId) return;

        const confirmed = await showConfirm({
            message: `"${subPlace.placeName}"을(를) 삭제하시겠습니까?`,
            confirmText: '삭제'
        });

        if (confirmed) {
            try {
                await MeetingRepository.onDeleteSubPlace(meetingId, subPlace.subPlaceId);
                showAlert({ message: '장소가 삭제되었습니다.' });
                fetchSubPlaces();
            } catch (error) {
                console.error('서브 장소 삭제 실패:', error);
                showAlert({ message: '장소 삭제에 실패했습니다.' });
            }
        }
    };

    // 참석 상태 변경 핸들러
    const handleAttendanceChange = async (newStatus: 'attending' | 'not_attending' | 'undecided') => {
        if (attendanceStatus === newStatus || !meetingId) return;

        const statusMap = {
            'attending': 'ATTENDING' as const,
            'not_attending': 'NOT_ATTENDING' as const,
            'undecided': 'UNDECIDED' as const
        };

        const statusLabel = {
            'attending': '참석',
            'not_attending': '불참',
            'undecided': '미정'
        };

        try {
            const response = await MeetingRepository.onPutUpdateAttendance(meetingId, statusMap[newStatus]);

            if (response?.status === 200) {
                setAttendanceStatus(newStatus);
                showAlert({ message: `${statusLabel[newStatus]}으로 변경되었습니다.` });
            } else {
                showAlert({ message: '참석 상태 변경에 실패했습니다.' });
            }
        } catch (error) {
            console.error('참석 상태 변경 실패:', error);
            showAlert({ message: '참석 상태 변경 중 오류가 발생했습니다.' });
        }
    };

    const handleLeave = async () => {
        const confirmed = await showConfirm({
            message: '정말 이 모임에서 나가시겠습니까?',
            confirmText: '나가기'
        });

        if (confirmed && meetingId) {
            try {
                console.log('모임 나가기 요청:', meetingId, 'Type:', typeof meetingId);
                const response = await MeetingRepository.onPostLeaveMeeting(meetingId);
                console.log('모임 나가기 응답:', response);

                if (response?.status === 200) {
                    await showAlert({ message: '모임에서 나갔습니다.' });
                    navigate('/my-meetings', { replace: true });
                } else if (response?.status === 400 && response?.data?.message) {
                    // 비즈니스 에러 (예: 모임 생성자는 나갈 수 없음)
                    await showAlert({ message: response.data.message });
                } else {
                    await showAlert({ message: '모임 나가기에 실패했습니다.' });
                }
            } catch (error) {
                console.error('모임 나가기 실패:', error);
                await showAlert({ message: '모임 나가기 중 오류가 발생했습니다.' });
            }
        }
    };

    const getStatusBadge = () => {
        switch (meeting.status) {
            case 'upcoming':
                return { text: '예정', className: 'upcoming' };
            default:
                return { text: '예정', className: 'upcoming' };
        }
    };

    const badge = getStatusBadge();

    return (
        <div className="meeting-detail-wrapper">
            <Header title="모임 상세" isSubPage />

            <div className="meeting-detail-content">
                {/* Meeting Header */}
                <div className="meeting-detail-header">
                    <div className="meeting-header-top">
                        <h1 className="meeting-name">{meeting.name}</h1>
                        <div className="meeting-badges">
                            {/* 모임 타입 배지 */}
                            <span className={`meeting-type-badge ${isTravelMeeting ? 'travel' : 'day'}`}>
                                {isTravelMeeting ? '✈️ 여행' : '📅 당일모임'}
                            </span>
                            {meeting.isRegularMeeting && (
                                <span className="meeting-regular-badge">🔄 정기모임</span>
                            )}
                            <span className={`meeting-status-badge ${badge.className}`}>
                                {badge.text}
                            </span>
                        </div>
                    </div>
                    {/* 여행 모임일 경우 여행지/기간 표시 */}
                    {isTravelMeeting && meeting.travelDestination && (
                        <div className="travel-destination-info">
                            <span className="destination-icon">📍</span>
                            <span className="destination-name">{meeting.travelDestination}</span>
                            {travelPeriod && (
                                <span className="travel-period-badge">
                                    {travelPeriod.nights}박 {travelPeriod.days}일
                                </span>
                            )}
                        </div>
                    )}
                    {meeting.description && (
                        <p className="meeting-description">{meeting.description}</p>
                    )}
                </div>

                {/* Tabs */}
                <div className="detail-tabs">
                    <button
                        className={`detail-tab ${activeTab === 'info' ? 'active' : ''}`}
                        onClick={() => setActiveTab('info')}
                    >
                        모임 정보
                    </button>
                    <button
                        className={`detail-tab ${activeTab === 'participants' ? 'active' : ''}`}
                        onClick={() => setActiveTab('participants')}
                    >
                        참여자 ({participants.length})
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'info' ? (
                    <div className="info-tab-content">
                        {/* 1시간 이내 시작하거나 진행 중인 모임 알림 배너 (모임정보 탭 내부 상단) */}
                        {upcomingSoonInfo && (
                            <div
                                className="upcoming-soon-banner"
                                onClick={() => navigate(`/meeting/location/${meetingId}`)}
                            >
                                <div className="upcoming-soon-icon">📍</div>
                                <div className="upcoming-soon-content">
                                    <div className="upcoming-soon-title">
                                        {upcomingSoonInfo.timeText
                                            ? `모임 ${upcomingSoonInfo.timeText} 전입니다`
                                            : '모임이 진행 중입니다'}
                                    </div>
                                    <div className="upcoming-soon-description">
                                        위치공유를 선택한 참여자의 위치를 확인하세요
                                    </div>
                                </div>
                                <div className="upcoming-soon-arrow">›</div>
                            </div>
                        )}

                        {/* Meeting Info Card - 여행 모임 */}
                        {isTravelMeeting ? (
                            <div className="info-card travel-info-card">
                                <div className="info-item">
                                    <span className="info-icon">📅</span>
                                    <div className="info-content">
                                        <div className="info-label">여행 기간</div>
                                        <div className="info-value">
                                            {travelPeriod ? (
                                                <>
                                                    {travelPeriod.start.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}
                                                    {' ~ '}
                                                    {travelPeriod.end.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}
                                                </>
                                            ) : '일정 미정'}
                                        </div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">⏰</span>
                                    <div className="info-content">
                                        <div className="info-label">집결 시간</div>
                                        <div className="info-value">{meeting.meetingTime || meeting.time || '시간 미정'}</div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">📍</span>
                                    <div className="info-content">
                                        <div className="info-label">집결 장소</div>
                                        <div className="info-value">{meeting.location}</div>
                                        <div className="info-subvalue">{meeting.locationDetail}</div>
                                    </div>
                                </div>

                                {meeting.hotelName && (
                                    <div className="info-item">
                                        <span className="info-icon">🏨</span>
                                        <div className="info-content">
                                            <div className="info-label">숙소</div>
                                            <div className="info-value">{meeting.hotelName}</div>
                                            <div className="info-subvalue">{meeting.hotelAddress}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="info-item">
                                    <span className="info-icon">👤</span>
                                    <div className="info-content">
                                        <div className="info-label">주최자</div>
                                        <div className="info-value">{meeting.createdBy}</div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">🎫</span>
                                    <div className="info-content">
                                        <div className="info-label">초대 코드</div>
                                        <div className="info-value">{meeting.inviteCode}</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Meeting Info Card - 당일 모임 */
                            <div className="info-card">
                                <div className="info-item">
                                    <span className="info-icon">📅</span>
                                    <div className="info-content">
                                        <div className="info-label">날짜</div>
                                        <div className="info-value">{meeting.date}</div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">⏰</span>
                                    <div className="info-content">
                                        <div className="info-label">시간</div>
                                        <div className="info-value">{meeting.time}</div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">📍</span>
                                    <div className="info-content">
                                        <div className="info-label">장소</div>
                                        <div className="info-value">{meeting.location}</div>
                                        <div className="info-subvalue">{meeting.locationDetail}</div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">👤</span>
                                    <div className="info-content">
                                        <div className="info-label">주최자</div>
                                        <div className="info-value">{meeting.createdBy}</div>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <span className="info-icon">🎫</span>
                                    <div className="info-content">
                                        <div className="info-label">초대 코드</div>
                                        <div className="info-value">{meeting.inviteCode}</div>
                                    </div>
                                </div>

                                {meeting.isRegularMeeting && (
                                    <div className="info-item">
                                        <span className="info-icon">🔄</span>
                                        <div className="info-content">
                                            <div className="info-label">반복 주기</div>
                                            <div className="info-value">
                                                {meeting.repeatType === 'WEEKLY' && meeting.repeatInterval === 1 && '매주'}
                                                {meeting.repeatType === 'WEEKLY' && meeting.repeatInterval === 2 && '격주'}
                                                {meeting.repeatType === 'MONTHLY' && '매월'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Location Info - 지도 상단 */}
                        {meeting.location && meeting.location !== '장소 미정' && (
                            <div className="location-info-section">
                                <div className="location-info-icon">📍</div>
                                <div className="location-info-content">
                                    <div className="location-info-name">{meeting.location}</div>
                                    <div className="location-info-address">
                                        {meeting.locationDetail || '상세 주소 없음'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Map Preview */}
                        <div
                            className={`map-preview ${!meeting.location || meeting.location === '장소 미정' ? 'no-location-info' : ''}`}
                            onClick={() => setShowMapModal(true)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="map-iframe-container">
                                <iframe
                                    className="map-iframe"
                                    src={`https://maps.google.com/maps?q=${encodeURIComponent(meeting.locationDetail || meeting.location)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Meeting Location Map"
                                    style={{ pointerEvents: 'none' }}
                                />
                                <div className="map-overlay">
                                    <span className="map-expand-hint">탭하여 큰 지도 보기</span>
                                </div>
                            </div>
                        </div>

                        {/* Attendance Status */}
                        <div className="attendance-section">
                            <h3 className="section-heading">나의 참석 여부</h3>
                            <div className="attendance-options">
                                <button
                                    className={`attendance-option ${attendanceStatus === 'attending' ? 'selected' : ''}`}
                                    onClick={() => handleAttendanceChange('attending')}
                                >
                                    <span className="attendance-icon">✅</span>
                                    <span className="attendance-label">참석</span>
                                </button>
                                <button
                                    className={`attendance-option ${attendanceStatus === 'not_attending' ? 'selected' : ''}`}
                                    onClick={() => handleAttendanceChange('not_attending')}
                                >
                                    <span className="attendance-icon">❌</span>
                                    <span className="attendance-label">불참</span>
                                </button>
                                <button
                                    className={`attendance-option ${attendanceStatus === 'undecided' ? 'selected' : ''}`}
                                    onClick={() => handleAttendanceChange('undecided')}
                                >
                                    <span className="attendance-icon">❓</span>
                                    <span className="attendance-label">미정</span>
                                </button>
                            </div>
                        </div>

                        {/* Location Sharing */}
                        <div className="location-sharing-section">
                            <div className="location-sharing-header">
                                <div className="location-sharing-info">
                                    <h3 className="section-heading">위치 공유</h3>
                                    <p className="section-description">실시간으로 내 위치를 공유합니다</p>
                                </div>
                                <button
                                    className={`toggle-switch ${isLocationSharing ? 'active' : ''}`}
                                    onClick={() => {
                                        const newValue = !isLocationSharing;
                                        setIsLocationSharing(newValue);
                                        // localStorage에 저장
                                        if (meetingId) {
                                            localStorage.setItem(`locationSharing_${meetingId}`, String(newValue));
                                        }
                                        // 사용자 피드백
                                        showAlert({
                                            message: newValue ? '위치 공유가 활성화되었습니다.' : '위치 공유가 비활성화되었습니다.'
                                        });
                                    }}
                                >
                                    <span className="toggle-slider"></span>
                                </button>
                            </div>
                        </div>

                        {/* 서브 장소 섹션 */}
                        <div className="sub-places-section">
                            <div className="sub-places-header">
                                <h3 className="section-heading">
                                    {isTravelMeeting ? '여행 일정' : '모임 장소'}
                                </h3>
                                <button className="add-sub-place-btn" onClick={handleAddSubPlace}>
                                    + 장소 추가
                                </button>
                            </div>

                            {!subPlaces || subPlaces.length === 0 ? (
                                <div className="sub-places-empty">
                                    <div className="empty-icon">{isTravelMeeting ? '🗺️' : '📍'}</div>
                                    <div className="empty-text">
                                        {isTravelMeeting
                                            ? '여행 일정에 방문할 장소를 추가해보세요'
                                            : '2차, 3차 장소를 추가해보세요'
                                        }
                                    </div>
                                    <button className="empty-add-btn" onClick={handleAddSubPlace}>
                                        장소 추가하기
                                    </button>
                                </div>
                            ) : (
                                <div className="sub-places-list">
                                    {/* 여행 모임: 일차별로 그룹핑 */}
                                    {isTravelMeeting ? (
                                        <>
                                            {Array.from(new Set(subPlaces.map(sp => sp.dayNumber || 1)))
                                                .sort((a, b) => a - b)
                                                .map(dayNum => (
                                                    <div key={dayNum} className="day-group">
                                                        <div className="day-header">
                                                            <span className="day-badge">DAY {dayNum}</span>
                                                            {travelPeriod && (
                                                                <span className="day-date">
                                                                    {new Date(travelPeriod.start.getTime() + (dayNum - 1) * 24 * 60 * 60 * 1000)
                                                                        .toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', weekday: 'short' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="day-places">
                                                            {subPlaces
                                                                .filter(sp => (sp.dayNumber || 1) === dayNum)
                                                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                                                .map(subPlace => (
                                                                    <div key={subPlace.subPlaceId} className="sub-place-item">
                                                                        <div className="sub-place-icon">{getCategoryIcon(subPlace.category)}</div>
                                                                        <div className="sub-place-content">
                                                                            <div className="sub-place-name">{subPlace.placeName}</div>
                                                                            <div className="sub-place-meta">
                                                                                <span className="sub-place-category">{getCategoryLabel(subPlace.category)}</span>
                                                                                {subPlace.visitTime && (
                                                                                    <span className="sub-place-time">{subPlace.visitTime}</span>
                                                                                )}
                                                                            </div>
                                                                            {subPlace.description && (
                                                                                <div className="sub-place-desc">{subPlace.description}</div>
                                                                            )}
                                                                        </div>
                                                                        <div className="sub-place-actions">
                                                                            <button
                                                                                className="sub-place-edit-btn"
                                                                                onClick={() => handleEditSubPlace(subPlace)}
                                                                            >
                                                                                ✏️
                                                                            </button>
                                                                            <button
                                                                                className="sub-place-delete-btn"
                                                                                onClick={() => handleDeleteSubPlace(subPlace)}
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                ))}
                                        </>
                                    ) : (
                                        /* 당일 모임: 카테고리별로 표시 */
                                        <>
                                            {subPlaces
                                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                                .map(subPlace => (
                                                    <div key={subPlace.subPlaceId} className="sub-place-item">
                                                        <div className="sub-place-icon">{getCategoryIcon(subPlace.category)}</div>
                                                        <div className="sub-place-content">
                                                            <div className="sub-place-name">{subPlace.placeName}</div>
                                                            <div className="sub-place-meta">
                                                                <span className="sub-place-category">{getCategoryLabel(subPlace.category)} 장소</span>
                                                                {subPlace.visitTime && (
                                                                    <span className="sub-place-time">{subPlace.visitTime}</span>
                                                                )}
                                                            </div>
                                                            {subPlace.placeAddress && (
                                                                <div className="sub-place-address">{subPlace.placeAddress}</div>
                                                            )}
                                                        </div>
                                                        <div className="sub-place-actions">
                                                            <button
                                                                className="sub-place-edit-btn"
                                                                onClick={() => handleEditSubPlace(subPlace)}
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                className="sub-place-delete-btn"
                                                                onClick={() => handleDeleteSubPlace(subPlace)}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="participants-tab-content">
                        <div className="participants-list">
                            {participants.map(participant => (
                                <div key={participant.id} className="participant-item">
                                    <div className="participant-avatar">
                                        {participant.name[0]}
                                    </div>
                                    <div className="participant-info">
                                        <div className="participant-name">
                                            {participant.name}
                                            {participant.isHost && (
                                                <span className="host-badge">주최자</span>
                                            )}
                                        </div>
                                        <div className={`participant-status ${participant.status}`}>
                                            {participant.status === 'confirmed' ? '참여 확정' : '대기 중'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="action-buttons">
                    <button className="action-btn primary" onClick={handleRealtimeLocation}>
                        <span className="action-btn-icon">📍</span>
                        <span className="action-btn-text">실시간 위치</span>
                    </button>

                    <button className="action-btn primary" onClick={handleChat}>
                        <span className="action-btn-icon">💬</span>
                        <span className="action-btn-text">채팅</span>
                    </button>

                    <button className="action-btn secondary" onClick={() => navigate(`/schedule/main/${meetingId}`, { state: { from: 'meeting-detail' } })}>
                        <span className="action-btn-icon">🕐</span>
                        <span className="action-btn-text">일정 조율</span>
                    </button>

                    <button className="action-btn secondary" onClick={() => navigate(`/cost/result/${meetingId}`)}>
                        <span className="action-btn-icon">💰</span>
                        <span className="action-btn-text">비용 정산</span>
                    </button>

                    <button className="action-btn secondary" onClick={handleEdit}>
                        <span className="action-btn-icon">✏️</span>
                        <span className="action-btn-text">수정</span>
                    </button>

                    <button className="action-btn secondary" onClick={handleShare}>
                        <span className="action-btn-icon">📤</span>
                        <span className="action-btn-text">공유</span>
                    </button>
                </div>

                {/* Leave Button */}
                <button className="leave-button" onClick={handleLeave}>
                    모임 나가기
                </button>
            </div>

            {/* Share Modal */}
            {showShareModal && (
                <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
                    <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <h2 className="share-modal-title">모임 공유하기</h2>
                            <button className="share-modal-close" onClick={() => setShowShareModal(false)}>✕</button>
                        </div>

                        <div className="share-modal-content">
                            {/* Invite Code Section */}
                            <div className="share-section">
                                <h3 className="share-section-title">초대 코드</h3>
                                <div className="invite-code-box">
                                    <span className="invite-code-text">{meeting.inviteCode}</span>
                                    <button className="copy-button" onClick={handleCopyInviteCode}>
                                        복사
                                    </button>
                                </div>
                            </div>

                            {/* Share Options */}
                            <div className="share-section">
                                <h3 className="share-section-title">공유 방법</h3>
                                <div className="share-options">
                                    <button className="share-option-btn kakao" onClick={handleShareKakao}>
                                        <span className="share-option-icon">💬</span>
                                        <span className="share-option-text">카카오톡</span>
                                    </button>
                                    <button className="share-option-btn sms" onClick={handleShareSMS}>
                                        <span className="share-option-icon">💬</span>
                                        <span className="share-option-text">문자</span>
                                    </button>
                                    <button className="share-option-btn link" onClick={handleCopyLink}>
                                        <span className="share-option-icon">🔗</span>
                                        <span className="share-option-text">링크 복사</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Map Modal */}
            {showMapModal && (
                <div className="map-modal-overlay" onClick={() => setShowMapModal(false)}>
                    <div className="map-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="map-modal-header">
                            <h2 className="map-modal-title">📍 {meeting.location}</h2>
                            <button className="map-modal-close" onClick={() => setShowMapModal(false)}>✕</button>
                        </div>
                        {meeting.locationDetail && (
                            <div className="map-modal-address">{meeting.locationDetail}</div>
                        )}
                        <div className="map-modal-content">
                            <iframe
                                className="map-modal-iframe"
                                src={`https://maps.google.com/maps?q=${encodeURIComponent(meeting.locationDetail || meeting.location)}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Meeting Location Map Full"
                            />
                        </div>
                        <div className="map-modal-actions">
                            <button
                                className="map-action-btn"
                                onClick={() => {
                                    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(meeting.locationDetail || meeting.location)}`, '_blank');
                                }}
                            >
                                Google Maps에서 열기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sub Place Add/Edit Modal */}
            <SubPlaceModal
                isOpen={showSubPlaceModal}
                onClose={handleCloseSubPlaceModal}
                meetingId={meetingId}
                editingSubPlace={editingSubPlace}
                isTravelMeeting={isTravelMeeting}
                travelPeriod={travelPeriod}
                meetingTime={isTravelMeeting ? meeting.meetingTime : meeting.time}
                subPlaces={subPlaces}
                onSaveSuccess={fetchSubPlaces}
                returnTo={`/meeting/detail/${meetingId}`}
                initialForm={subPlaceForm}
                initialIsCustomVisitTime={isCustomVisitTime}
            />
        </div>
    );
};
